'use client';

import { Button } from '@curology/ui-components-web/react/Button';
import { Checkbox } from '@curology/ui-components-web/react/Checkbox';
import { Highlight } from '@curology/ui-components-web/react/Highlight';
import { PhoneInput } from '@curology/ui-components-web/react/PhoneInput';
import { TextInput } from '@curology/ui-components-web/react/TextInput';
import { Typography } from '@curology/ui-components-web/react/Typography';
import { useFormState, useFormStatus } from 'react-dom';

import { useDataProvider } from '@/components/flow/components/DataProvider/useDataProvider';
import LegalLinks from '@/components/legalLinks';
import { DATA_KEYS } from '@/consts/dataKeys';

import { saveGuardianInformation } from './actions';
import { useFlowContext } from '../../hooks/useFlowContext';
import { useFlowUrl } from '../../hooks/useFlowUrl';

export const GuardianForm = ({ redirectUrl }: { redirectUrl?: string }) => {
  const guardian = useDataProvider(DATA_KEYS.guardian);
  const [errorBag, action] = useFormState(saveGuardianInformation, null);
  const { nextStepRoute } = useFlowContext();
  const nextStepUrl = useFlowUrl({ type: 'nextJsPath', route: nextStepRoute });

  return (
    <>
      {errorBag?.message && (
        <Highlight state="critical" size="sm" className="cur-text-center">
          {errorBag.message}
        </Highlight>
      )}
      <form
        action={action}
        className="cur-flex cur-flex-col cur-gap-6 data-hj-suppress"
      >
        <input
          type="hidden"
          name="redirectUrl"
          data-testid="redirectUrl"
          value={redirectUrl ?? nextStepUrl}
        />
        <div className="cur-grid md:cur-grid-cols-2 cur-gap-2">
          <TextInput
            required
            label="Guardian's first name"
            placeholder="Jane"
            id="firstname"
            name="firstname"
            value={guardian?.firstname}
            state={errorBag?.errors?.firstname && 'critical'}
            helperText={errorBag?.errors?.firstname?.[0]}
          />
          <TextInput
            required
            label="Guardian's last name"
            placeholder="Doe"
            id="lastname"
            name="lastname"
            value={guardian?.lastname}
            state={errorBag?.errors?.lastname && 'critical'}
            helperText={errorBag?.errors?.lastname?.[0]}
          />
          <TextInput
            className="md:cur-col-span-2"
            required
            label="Guardian's email"
            placeholder="jane.doe@example.com"
            type="email"
            id="email"
            name="email"
            value={guardian?.email}
            state={errorBag?.errors?.email && 'critical'}
            helperText={errorBag?.errors?.email?.[0]}
          />
          <PhoneInput
            required
            className="md:cur-col-span-2"
            label="Guardian's phone"
            id="phone"
            name="phone"
            value={guardian?.phone}
            state={errorBag?.errors?.phone && 'critical'}
            helperText={errorBag?.errors?.phone?.[0]}
          />
        </div>
        <Typography size="body" className="cur-mt-3 cur-text-primary">
          Hey, parent or guardian—welcome to Curology! A licensed dermatology
          provider will create a custom formula for your teen’s skin, based on
          his or her needs and skin goals. Do you agree to accept Curology’s
          treatment of your teen?
        </Typography>
        <Checkbox
          name="terms"
          value="1"
          aria-label={
            'I am the parent or guardian, and I agree to the Terms of Service, Privacy Policy and Telehealth Consent'
          }
          className="cur-px-2 [&>div>div]:cur-mt-[6px]"
          meta={errorBag?.errors?.terms?.[0]}
          state={errorBag?.errors?.terms?.[0] && 'critical'}
          label={
            <div className="cur-text-primary">
              I am the parent or guardian, and I agree to the{' '}
              <LegalLinks.TermsOfService brand="curology" />
              {', '}
              <LegalLinks.PrivacyPolicy brand="curology" />
              {' and '}
              <LegalLinks.TelehealthConsent brand="curology" />.
            </div>
          }
        />
        <SubmitButton />
      </form>
    </>
  );
};

const SubmitButton = () => {
  const { pending } = useFormStatus();
  return (
    <Button
      type="submit"
      className="cur-min-w-[300px] cur-mx-auto cur-my-2"
      loading={pending}
      disabled={pending}
    >
      Next
    </Button>
  );
};
